import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowRight,
  FiCheck,
  FiLink,
  FiPackage,
  FiShoppingBag,
  FiTrendingUp,
  FiUsers
} from 'react-icons/fi';
import { usePageSEO } from '../hooks/useSEO';

const DEFAULT_LINEAR_PERCENTS = [5, 0, 0, 0, 0, 0];

function formatLevelsLabel(percents) {
  const active = percents
    .map((p, i) => ({ p: Number(p) || 0, i: i + 1 }))
    .filter((x) => x.p > 0);
  if (active.length === 0) return 'линейные начисления временно отключены';
  if (active.length === 1) {
    return `${active[0].p}% с заказов 1-го уровня (лично приглашённых)`;
  }
  return active.map((x) => `${x.i}-й: ${x.p}%`).join(' · ');
}

function buildMarketingParts(percents) {
  const linearText = formatLevelsLabel(percents);
  return [
    {
      number: '01',
      title: 'Линейное вознаграждение',
      text: `Сейчас в программе действует линейный бонус: ${linearText}. Ставки берутся из актуальных настроек.`
    },
    {
      number: '02',
      title: 'Персональная ссылка',
      text: 'Оплаченные заказы покупателей по вашей ссылке учитываются в личном кабинете.'
    },
    {
      number: '03',
      title: 'Начисление в кабинете',
      text: 'Итог появляется в личном кабинете сразу после оплаты заказа.'
    }
  ];
}

const Page = styled.main`
  --ref-bg: #f5f1e9;
  --ref-surface: #fff;
  --ref-ink: #20211e;
  --ref-muted: #666961;
  --ref-line: #ddd7cc;
  --ref-accent: #b65a3a;
  --ref-sage: #657456;

  overflow: hidden;
  background: var(--ref-bg);
  color: var(--ref-ink);
`;

const Hero = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 690px;
  margin: 16px auto 0;
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  background: #1d201c;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(20, 22, 19, .97) 0%, rgba(20, 22, 19, .88) 38%, rgba(20, 22, 19, .27) 68%, rgba(20, 22, 19, .05));
  }

  @media (max-width: 760px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    display: block;
    background: #1d201c;

    &::after { display: none; }
  }
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 760px) {
    position: relative;
    height: 310px;
    object-position: 67% center;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  width: min(1240px, calc(100% - 96px));
  margin: 0 auto;
  padding: 90px 0;

  @media (max-width: 760px) {
    width: 100%;
    padding: 50px 20px 62px;
  }
`;

const HeroCopy = styled.div`
  max-width: 720px;

  h1 {
    max-width: 700px;
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: clamp(58px, 7vw, 98px);
    line-height: .9;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p {
    max-width: 620px;
    margin: 26px 0 0;
    color: rgba(255, 255, 255, .76);
    font-size: 18px;
    line-height: 1.65;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 15px !important;
  color: #e0ad64 !important;
  font-size: 12px !important;
  line-height: 1.2 !important;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Button = styled(Link)`
  min-height: 52px;
  padding: 0 22px;
  border: 1px solid ${props => props.$primary ? '#b65a3a' : 'rgba(255,255,255,.28)'};
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.$primary ? '#b65a3a' : 'transparent'};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  transition: transform 180ms ease, background 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$primary ? '#98472f' : 'rgba(255,255,255,.1)'};
  }

  &:focus-visible { outline: 3px solid #fff; outline-offset: 3px; }
`;

const AnchorButton = styled.a`
  min-height: 52px;
  padding: 0 22px;
  border: 1px solid rgba(255,255,255,.28);
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: 800;

  &:hover { background: rgba(255,255,255,.1); }
`;

const IntroStrip = styled.section`
  position: relative;
  z-index: 4;
  width: min(1180px, calc(100% - 48px));
  margin: -42px auto 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid rgba(32, 33, 30, .1);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 62px rgba(33, 30, 25, .13);

  @media (max-width: 760px) {
    width: min(100% - 32px, 1180px);
    margin-top: 24px;
    grid-template-columns: 1fr;
  }
`;

const IntroItem = styled.div`
  min-height: 170px;
  padding: 28px;
  border-right: 1px solid #e7e1d8;

  &:last-child { border-right: 0; }

  svg { width: 25px; height: 25px; color: var(--ref-accent); }
  strong { display: block; margin-top: 24px; font-size: 17px; }
  span { display: block; margin-top: 8px; color: var(--ref-muted); font-size: 14px; line-height: 1.55; }

  @media (max-width: 760px) {
    min-height: 0;
    border-right: 0;
    border-bottom: 1px solid #e7e1d8;
    &:last-child { border-bottom: 0; }
  }
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
  padding: clamp(78px, 8vw, 120px) 0;

  @media (max-width: 700px) { width: min(100% - 32px, 1180px); }
`;

const SectionHead = styled.header`
  max-width: 760px;
  margin-bottom: 44px;

  span {
    color: #8f432d;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .16em;
    text-transform: uppercase;
  }

  h2 {
    margin: 14px 0 0;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: clamp(48px, 5.5vw, 76px);
    line-height: .94;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  p {
    max-width: 680px;
    margin: 20px 0 0;
    color: var(--ref-muted);
    font-size: 17px;
    line-height: 1.65;
  }
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

const Step = styled.article`
  position: relative;
  min-height: 290px;
  padding: 30px;
  overflow: hidden;
  border: 1px solid var(--ref-line);
  border-radius: 18px;
  background: ${props => props.$dark ? '#232620' : '#fff'};
  color: ${props => props.$dark ? '#fff' : 'var(--ref-ink)'};

  &::after {
    content: '${props => props.$number}';
    position: absolute;
    top: 8px;
    right: 16px;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: 100px;
    line-height: 1;
    opacity: .07;
  }

  svg { width: 28px; height: 28px; color: ${props => props.$dark ? '#e0ad64' : 'var(--ref-accent)'}; }
  h3 { margin: 92px 0 12px; font-size: 20px; line-height: 1.25; }
  p { margin: 0; color: inherit; font-size: 14px; line-height: 1.6; opacity: .7; }
`;

const WideStory = styled.section`
  width: min(1400px, calc(100% - 32px));
  min-height: 620px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 26px;
  display: grid;
  grid-template-columns: .9fr 1.1fr;
  background: #242620;
  color: #fff;

  @media (max-width: 820px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    grid-template-columns: 1fr;
  }
`;

const WideCopy = styled.div`
  padding: clamp(42px, 6vw, 82px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 {
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: clamp(50px, 5.5vw, 78px);
    line-height: .94;
    text-transform: uppercase;
  }

  p { margin: 24px 0 0; color: rgba(255,255,255,.72); font-size: 16px; line-height: 1.65; }
`;

const WideImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 620px;
  display: block;
  object-fit: cover;
  object-position: 66% center;

  @media (max-width: 820px) { min-height: 340px; order: -1; }
`;

const MarketingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 860px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const MarketingCard = styled.article`
  position: relative;
  min-height: 250px;
  padding: 28px;
  overflow: hidden;
  border: 1px solid var(--ref-line);
  border-radius: 16px;
  background: #fff;

  > span {
    position: absolute;
    top: 8px;
    right: 14px;
    color: var(--ref-accent);
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: 84px;
    line-height: 1;
    opacity: .12;
  }

  h3 { margin: 86px 0 12px; font-size: 19px; }
  p { margin: 0; color: var(--ref-muted); font-size: 14px; line-height: 1.6; }
`;

const Levels = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid var(--ref-line);
  border-radius: 18px;
  background: #fff;

  @media (max-width: 760px) { grid-template-columns: repeat(3, 1fr); }
`;

const Level = styled.div`
  min-height: 150px;
  padding: 24px 14px;
  border-right: 1px solid #e8e2d8;
  text-align: center;

  &:last-child { border-right: 0; }
  strong { display: block; color: var(--ref-accent); font-size: clamp(30px, 4vw, 46px); line-height: 1; }
  span { display: block; margin-top: 14px; color: var(--ref-muted); font-size: 12px; }

  @media (max-width: 760px) {
    &:nth-child(3) { border-right: 0; }
    &:nth-child(-n + 3) { border-bottom: 1px solid #e8e2d8; }
  }
`;

const Activity = styled.div`
  margin-top: 26px;
  padding: 28px;
  border-radius: 18px;
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 18px;
  background: #e6ebde;

  svg { width: 28px; height: 28px; color: var(--ref-sage); }
  strong { display: block; font-size: 18px; }
  p { margin: 8px 0 0; color: #5d6257; font-size: 14px; line-height: 1.6; }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid var(--ref-line);
  border-radius: 18px;
  background: #fff;

  table { width: 100%; min-width: 760px; border-collapse: collapse; }
  th, td { padding: 18px 20px; border-bottom: 1px solid #ece7de; text-align: left; vertical-align: top; }
  th { color: #77796f; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; }
  td { color: #565950; font-size: 14px; line-height: 1.5; }
  td:first-child { color: var(--ref-ink); font-weight: 800; }
  tr:last-child td { border-bottom: 0; }
`;

const Final = styled.section`
  padding: clamp(82px, 10vw, 138px) 24px;
  background: #181a17;
  color: #fff;
  text-align: center;

  h2 {
    max-width: 920px;
    margin: 0 auto;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: clamp(58px, 7vw, 96px);
    line-height: .92;
    text-transform: uppercase;
  }

  p { max-width: 620px; margin: 24px auto 0; color: rgba(255,255,255,.68); font-size: 16px; line-height: 1.65; }
  ${Actions} { justify-content: center; }
`;

const Disclaimer = styled.p`
  max-width: 900px;
  margin: 42px auto 0;
  color: #878980;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
`;

const Referral = () => {
  usePageSEO({
    title: 'Партнёрская программа Lagom Life — как всё устроено',
    description: 'Понятно о партнёрской программе Lagom Life: персональная ссылка, заказы покупателей и уровни вознаграждений.'
  });

  const [linearPercents, setLinearPercents] = useState(DEFAULT_LINEAR_PERCENTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/settings');
        if (!res.ok) return;
        const data = await res.json();
        const s = data.settings || {};
        const next = [
          s.referralLevel1Percent,
          s.referralLevel2Percent,
          s.referralLevel3Percent,
          s.referralLevel4Percent,
          s.referralLevel5Percent,
          s.referralLevel6Percent
        ].map((v, i) => {
          const n = parseInt(v, 10);
          return Number.isFinite(n) ? n : DEFAULT_LINEAR_PERCENTS[i];
        });
        if (!cancelled) setLinearPercents(next);
      } catch (_) {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const marketingParts = useMemo(() => buildMarketingParts(linearPercents), [linearPercents]);
  const visibleLevels = useMemo(
    () => linearPercents
      .map((percent, index) => ({ percent: Number(percent) || 0, index }))
      .filter((x) => x.percent > 0),
    [linearPercents]
  );
  const activeLevels = visibleLevels.map((x) => x.percent);
  const linearHeadline = activeLevels.length === 1
    ? `${activeLevels[0]}% с первого уровня`
    : activeLevels.length === 0
      ? 'Линейные ставки задаются в программе'
      : formatLevelsLabel(linearPercents);

  return (
    <Page>
      <Hero>
        <HeroImage src="/slider/hero-partnership-v3.png" alt="Партнёры обсуждают продукты Lagom Life на кухне" />
        <HeroInner>
          <HeroCopy>
            <Eyebrow>Партнёрская программа Lagom Life</Eyebrow>
            <h1>Партнёрская программа без собственного склада</h1>
            <p>
              Персональная ссылка связывает оплаченные заказы с вашим аккаунтом. FoodStar принимает оплату, собирает и доставляет заказ, а начисление отражается в личном кабинете по правилам программы.
            </p>
            <Actions>
              <Button to="/register" $primary>Стать партнёром <FiArrowRight aria-hidden="true" /></Button>
              <AnchorButton href="#how-it-works">Условия участия</AnchorButton>
            </Actions>
          </HeroCopy>
        </HeroInner>
      </Hero>

      <IntroStrip aria-label="Коротко о партнёрской программе">
        <IntroItem><FiLink aria-hidden="true" /><strong>Персональная ссылка</strong><span>Заказы по ней учитываются в вашем аккаунте.</span></IntroItem>
        <IntroItem><FiShoppingBag aria-hidden="true" /><strong>Оформление на foodstar.pro</strong><span>Оплата, сборка и доставка остаются на стороне магазина.</span></IntroItem>
        <IntroItem><FiTrendingUp aria-hidden="true" /><strong>Начисления за заказы</strong><span>Линейный процент с оплаченных заказов по вашей ссылке.</span></IntroItem>
      </IntroStrip>

      <Section id="how-it-works">
        <SectionHead>
          <span>Старт в программе</span>
          <h2>Личный заказ открывает доступ к персональной ссылке</h2>
          <p>После регистрации в личном кабинете доступна ссылка, по которой программа учитывает оплаченные заказы покупателей.</p>
        </SectionHead>
        <Steps>
          <Step $number="01" $dark><FiPackage aria-hidden="true" /><h3>Регистрация и личный заказ</h3><p>Открывает доступ к партнёрской программе и персональной ссылке в кабинете.</p></Step>
          <Step $number="02"><FiLink aria-hidden="true" /><h3>Персональная ссылка</h3><p>Ссылка доступна в личном кабинете и связывает заказы покупателей с вашим аккаунтом.</p></Step>
          <Step $number="03"><FiTrendingUp aria-hidden="true" /><h3>Начисления в личном кабинете</h3><p>Оплаченные заказы и связанные с ними начисления отражаются по правилам программы.</p></Step>
        </Steps>
      </Section>

      <WideStory>
        <WideCopy>
          <Eyebrow>Без склада и поездок по городу</Eyebrow>
          <h2>Покупатель общается с магазином напрямую</h2>
          <p>Компания принимает оплату, собирает заказ и организует доставку. Хранение товара, приём оплаты и доставка не входят в работу партнёра.</p>
          <Actions><Button to="/delivery" $primary>Посмотреть доставку <FiArrowRight aria-hidden="true" /></Button></Actions>
        </WideCopy>
        <WideImage src="/referral/order-flow.png" alt="Оформление заказа Lagom Life через интернет-магазин" loading="lazy" />
      </WideStory>

      <Section>
        <SectionHead>
          <span>Из чего складывается вознаграждение</span>
          <h2>Сейчас — линейный процент с заказов приглашённых</h2>
          <p>Актуальные ставки задаются в настройках программы. Другие виды бонусов на этой странице не описываем — действует то, что видно ниже.</p>
        </SectionHead>
        <MarketingGrid>
          {marketingParts.map(part => (
            <MarketingCard key={part.number}>
              <span>{part.number}</span>
              <h3>{part.title}</h3>
              <p>{part.text}</p>
            </MarketingCard>
          ))}
        </MarketingGrid>
      </Section>

      <WideStory>
        <WideImage src="/referral/team-growth.png" alt="Партнёры Lagom Life обсуждают продукты" loading="lazy" />
        <WideCopy>
          <Eyebrow>Команда</Eyebrow>
          <h2>Приглашайте людей, которым полезны продукты</h2>
          <p>Помогайте с выбором, делитесь персональной ссылкой и смотрите начисления в личном кабинете.</p>
        </WideCopy>
      </WideStory>

      <Section>
        <SectionHead>
          <span>Линейная часть</span>
          <h2>{linearHeadline}</h2>
          <p>
            Процент с заказов лично приглашённых. Ставки берутся из актуальных настроек программы
            и считаются с оплаченных заказов.
          </p>
        </SectionHead>
        <Levels>
          {visibleLevels.length > 0 ? visibleLevels.map(({ percent, index }) => (
            <Level key={`level-${index}`}>
              <strong>{percent}%</strong>
              <span>{index + 1}-й уровень</span>
            </Level>
          )) : (
            <Level key="empty">
              <strong>—</strong>
              <span>ставки уточняйте в программе</span>
            </Level>
          )}
        </Levels>
        <Activity>
          <FiCheck aria-hidden="true" />
          <div>
            <strong>Начисления — сразу после оплаты</strong>
            <p>Линейный процент считается с оплаченных заказов приглашённых и сразу отражается в личном кабинете.</p>
          </div>
        </Activity>
        <Disclaimer>Точные условия, расчётные периоды и ограничения определяются действующей редакцией правил партнёрской программы Lagom Life. Примеры не являются гарантией дохода.</Disclaimer>
      </Section>

      <Final>
        <FiUsers size={30} aria-hidden="true" />
        <h2>Партнёрство начинается с личного опыта</h2>
        <p>Регистрация и личный заказ открывают доступ к персональной ссылке и начислениям по правилам программы.</p>
        <Actions>
          <Button to="/register" $primary>Стать партнёром <FiArrowRight aria-hidden="true" /></Button>
          <Button to="/catalog">Каталог продукции</Button>
        </Actions>
      </Final>
    </Page>
  );
};

export default Referral;
